import {
  queryProjectByUuid,
  queryProjects,
} from '@/services/ant-design-pro/api';
import { t as i18nT } from '@/utils/i18n';

const PROJECT_STORAGE_KEY = 'ab.currentProjectId';

let projectCache: API.Project[] = [];

const mergeProjectIntoCache = (project: API.Project) => {
  const nextCache = projectCache.filter(
    (cachedProject) => cachedProject.uuid !== project.uuid,
  );
  nextCache.unshift(project);
  projectCache = nextCache;
};

const normalizeProjectList = (
  payload:
    | API.QueryProjectsResponse
    | { data?: API.QueryProjectsResponse }
    | API.Project[],
) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (payload?.data && Array.isArray(payload.data.data)) {
    return payload.data.data;
  }
  return [];
};

export const fetchProjectList = async (
  search?: string,
): Promise<API.Project[]> => {
  let orgUuid: string | undefined;
  if (typeof window !== 'undefined') {
    const userStr = window.localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        orgUuid =
          user?.organizationUuid ||
          user?.lastLoginOrganizationUuid ||
          user?.tenantUuid;
      } catch (e) {
        console.error(i18nT('auto.pages.pages.projects.data.1'), e);
      }
    }
  }

  const response = await queryProjects({
    page: 1,
    limit: 100,
    search: search?.trim() || undefined,
    orgUuid,
  });

  const list = normalizeProjectList(response).filter(
    (project) => !project.isDeleted,
  );

  projectCache = list;
  return list;
};

export const getCachedProjectList = () => projectCache;

export const getProjectById = async (projectId?: string) => {
  if (!projectId) {
    if (projectCache.length > 0) {
      return projectCache[0];
    }
    const list = await fetchProjectList();
    return list[0];
  }

  const fromCache = projectCache.find((project) => project.uuid === projectId);
  if (fromCache) {
    return fromCache;
  }

  const project = await queryProjectByUuid(projectId);
  if (project && !project.isDeleted) {
    mergeProjectIntoCache(project);
    return project;
  }

  return undefined;
};

export const getDefaultProjectId = async () => {
  if (typeof window !== 'undefined') {
    const fromStorage = window.localStorage.getItem(PROJECT_STORAGE_KEY);
    if (fromStorage) {
      return fromStorage;
    }
  }

  const firstProject = await getProjectById();
  return firstProject?.uuid ?? '';
};
